import { z } from 'zod';
import DOMPurify from 'dompurify';

/**
 * Zod schemas for the `contactForm` block (Phase 7c).
 *
 * One discriminated-union schema per `variant`:
 *  - `contact-us`    -- general contact (firstName, lastName, email, message)
 *  - `contact-sales` -- B2B sales inquiry (adds company, title, audience,
 *                       campaignType, destination)
 *  - `optout`        -- CCPA / GDPR data-rights request (email, name?,
 *                       requestType, jurisdiction)
 *
 * All string fields run through a DOMPurify-based `sanitize` transform so
 * any HTML markup is stripped before submission (CWE-79/80 protection).
 * Mirrors the per-page sanitization patterns in
 * `src/pages/ContactUs.tsx`, `src/pages/ContactSales.tsx`, and
 * `src/pages/Optout.tsx`.
 */

/**
 * Strip all HTML tags from a string. Wrapped in a guard so the schema
 * still works in non-browser environments (Vitest, future SSR) where
 * `DOMPurify` may not have `window` available -- in that case we fall
 * back to a defense-in-depth regex strip.
 *
 * The fallback strips dangerous tag-bodies (script, style, iframe,
 * object, embed, link, meta, on* event handlers) BEFORE stripping
 * remaining markup, so input like `<script>alert('xss')</script>`
 * yields `''` instead of `"alert('xss')"`. This matches the DOMPurify
 * behavior for `ALLOWED_TAGS: []`.
 *
 * The fallback is best-effort; production code paths always go through
 * DOMPurify because `window` is defined in the browser AND in our
 * jsdom-backed Vitest setup.
 */
const DANGEROUS_TAG_PATTERN =
  /<(script|style|iframe|object|embed|link|meta|svg|math)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const SELF_CLOSING_DANGEROUS_PATTERN =
  /<(script|style|iframe|object|embed|link|meta|svg|math)\b[^>]*\/?\s*>/gi;

const sanitize = (value: string): string => {
  if (typeof DOMPurify.sanitize === 'function' && typeof window !== 'undefined') {
    return DOMPurify.sanitize(value, { ALLOWED_TAGS: [] });
  }
  return value
    .replace(DANGEROUS_TAG_PATTERN, '')
    .replace(SELF_CLOSING_DANGEROUS_PATTERN, '')
    .replace(/<[^>]*>/g, '');
};

const optionalSanitizedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value ? sanitize(value) : undefined));

export const contactUsFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(50)
    .transform(sanitize),
  lastName: z
    .string()
    .trim()
    .min(1, 'Last name is required')
    .max(50)
    .transform(sanitize),
  email: z
    .string()
    .trim()
    .email('Please enter a valid email')
    .max(255)
    .transform(sanitize),
  message: z
    .string()
    .trim()
    .min(10, 'Message must be at least 10 characters')
    .max(1000)
    .transform(sanitize),
});

export type ContactUsFormData = z.infer<typeof contactUsFormSchema>;

export const contactSalesFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(50)
    .transform(sanitize),
  lastName: z
    .string()
    .trim()
    .min(1, 'Last name is required')
    .max(50)
    .transform(sanitize),
  email: z
    .string()
    .trim()
    .email('Please enter a valid email')
    .max(255)
    .transform(sanitize),
  company: optionalSanitizedString(100),
  title: optionalSanitizedString(100),
  audience: optionalSanitizedString(500),
  campaignType: optionalSanitizedString(100),
  destination: optionalSanitizedString(200),
  message: optionalSanitizedString(1000),
  // REK-1729: optional marketing-opt-in checkbox. Submitting WITHOUT
  // consent still files the lead through the unchanged contact-sales
  // pipeline; only the additive Braze write is gated on `=== true`.
  // Defaulted to `false` so omitting the field on the wire is a clean
  // "no consent" rather than `undefined` ambiguity.
  marketingConsent: z.boolean().optional().default(false),
});

export type ContactSalesFormData = z.infer<typeof contactSalesFormSchema>;

export const optoutFormSchema = z.object({
  email: z
    .string()
    .trim()
    .email('Please enter a valid email address')
    .max(255, 'Email is too long')
    .transform(sanitize),
  name: optionalSanitizedString(100),
  requestType: z.enum(['do_not_sell', 'delete', 'access'], {
    required_error: 'Please select a request type',
  }),
  jurisdiction: optionalSanitizedString(100),
});

export type OptoutFormData = z.infer<typeof optoutFormSchema>;

export type ContactFormVariant = 'contact-us' | 'contact-sales' | 'optout';

export type ContactFormData =
  | ContactUsFormData
  | ContactSalesFormData
  | OptoutFormData;

/**
 * Resolve the appropriate Zod schema for a given variant. Used by the
 * `contactForm` block to pick the right validation schema at render time.
 */
export function schemaForVariant(variant: ContactFormVariant) {
  switch (variant) {
    case 'contact-us':
      return contactUsFormSchema;
    case 'contact-sales':
      return contactSalesFormSchema;
    case 'optout':
      return optoutFormSchema;
  }
}
